import { toast } from "@/components/ui/use-toast";
import { useCallback, useRef, useState } from "react";
import * as XLSX from "xlsx";
import type {
  NormalizedStore,
  StoreMatchResult,
  StorePreviewData,
} from "@/@types/campaign";

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ACCEPTED_HEADERS = new Set([
  "loja",
  "codigoloja",
  "codloja",
  "codigo",
]);

const normalizeHeader = (value: unknown): string =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[\s_]+/g, "")
    .trim()
    .toLowerCase();

const isNumeric = (value: string): boolean => /^\d+$/.test(value);

const matchStores = (
  values: string[],
  normalizedStores: NormalizedStore[],
): StoreMatchResult => {
  const byCode = new Map(normalizedStores.map(store => [store.code, store]));
  const matched = new Map<number, NormalizedStore>();
  const notFound: string[] = [];

  values.forEach(value => {
    if (isNumeric(value)) {
      const store = byCode.get(parseInt(value, 10));
      if (store) {
        matched.set(store.code, store);
        return;
      }
      notFound.push(value);
      return;
    }

    const term = value.toLowerCase();
    const hits = normalizedStores.filter(store =>
      store.name.toLowerCase().includes(term),
    );
    if (hits.length === 0) {
      notFound.push(value);
      return;
    }
    hits.forEach(store => matched.set(store.code, store));
  });

  return { matchedStores: Array.from(matched.values()), notFound };
};

const extractStoreValues = (rows: unknown[][]): string[] | null => {
  if (!rows.length) return null;

  const header = rows[0] ?? [];
  const columnIndex = header.findIndex(cell =>
    ACCEPTED_HEADERS.has(normalizeHeader(cell)),
  );
  if (columnIndex === -1) return null;

  const seen = new Set<string>();
  const values: string[] = [];
  rows.slice(1).forEach(row => {
    const raw = row?.[columnIndex];
    const value = String(raw ?? "").trim();
    if (!value || seen.has(value.toLowerCase())) return;
    seen.add(value.toLowerCase());
    values.push(value);
  });

  return values;
};

export const buildStoreTemplate = (): void => {
  const link = document.createElement("a");
  link.href = "/templates/store-import-template.xlsx";
  link.download = "store-import-template.xlsx";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export function useStoreExcelImport(
  normalizedStores: NormalizedStore[],
  selectedStores: number[],
) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [preview, setPreview] = useState<StorePreviewData | null>(null);

  const clearFileInput = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
        toast({
          title: "Arquivo inválido",
          description: "Por favor, envie um arquivo Excel (.xlsx ou .xls)",
          variant: "destructive",
        });
        clearFileInput();
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: "Arquivo muito grande",
          description: "O arquivo não pode exceder 10MB.",
          variant: "destructive",
        });
        clearFileInput();
        return;
      }

      setIsProcessing(true);

      try {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = sheetName ? workbook.Sheets[sheetName] : undefined;

        const rows = worksheet
          ? (XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][])
          : [];

        const values = extractStoreValues(rows);
        if (values === null) {
          toast({
            title: "Arquivo inválido",
            description:
              "Certifique-se de que existe coluna 'Loja' ou 'Código'.",
            variant: "destructive",
          });
          return;
        }

        const { matchedStores, notFound } = matchStores(values, normalizedStores);
        const selectedSet = new Set(selectedStores);

        setPreview({
          matchedCodes: matchedStores.map(store => store.code),
          matchedStores,
          alreadySelected: matchedStores
            .filter(store => selectedSet.has(store.code))
            .map(store => store.code),
          notFound,
        });
      } catch {
        toast({
          title: "Erro ao importar",
          description: "Não foi possível ler o arquivo Excel.",
          variant: "destructive",
        });
      } finally {
        setIsProcessing(false);
        clearFileInput();
      }
    },
    [normalizedStores, selectedStores, clearFileInput],
  );

  const closePreview = useCallback(() => setPreview(null), []);

  const confirmImport = useCallback(
    (matchedCodes: number[], onApply: (codes: number[]) => void): void => {
      const union = Array.from(new Set([...selectedStores, ...matchedCodes]));
      onApply(union);
    },
    [selectedStores],
  );

  return {
    fileInputRef,
    isProcessing,
    preview,
    handleFileUpload,
    closePreview,
    confirmImport,
  };
}
